import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { useBusinessContext } from "@/providers/business-provider";
import { useLoading } from "@/providers/loading-provider";
import { apiRequest } from "@/lib/queryClient";
import { getInitials } from "@/lib/utils";
import {
  Camera, Loader2, Save, Pencil, X, Building2, FileText,
  Phone, Users, CreditCard, MapPin, UserCircle, Landmark, Send, CheckCircle2, Clock,
} from "lucide-react";

import { flattenIndustries } from "@shared/constants/industries";
const INDUSTRIES = flattenIndustries();
const ENTITY_TYPES = ["LLC", "S-Corp", "C-Corp", "Sole Proprietor", "Partnership", "Non-Profit", "Other"];
const CONTACT_ROLES = ["Owner", "Manager", "Accountant", "Bookkeeper", "CFO", "Other"];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

type Form = {
  name: string; industry: string; naicsCode: string; entityType: string;
  ein: string; stateId: string; formationState: string;
  businessPhone: string; businessEmail: string;
  addressLine1: string; addressLine2: string;
  city: string; state: string; zip: string; country: string;
  contactName: string; contactEmail: string; contactPhone: string; contactRole: string;
};

function seedForm(p: any): Form {
  return {
    name: p.name || "", industry: p.industry || "", naicsCode: p.naics_code || "", entityType: p.entity_type || "",
    ein: p.ein || "", stateId: p.state_id || "", formationState: p.formation_state || "",
    businessPhone: p.business_phone || p.phone_number || "", businessEmail: p.business_email || p.email || "",
    addressLine1: p.address_line1 || "", addressLine2: p.address_line2 || "",
    city: p.city || "", state: p.state || "", zip: p.zip || "", country: p.country || "US",
    contactName: p.contact_name || "", contactEmail: p.contact_email || "",
    contactPhone: p.contact_phone || "", contactRole: p.contact_role || "",
  };
}

const EMPTY: Form = seedForm({});

export default function BusinessProfilePage() {
  const { activeBusiness } = useBusinessContext();
  const { toast } = useToast();
  const { navigateTo } = useLoading();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const { data: profile, isLoading } = useQuery<any>({
    queryKey: ["/api/business/profile", activeBusiness?.id],
    queryFn: async () => { const res = await apiRequest("GET", "/api/business/profile"); return res.json(); },
    enabled: !!activeBusiness?.id,
  });

  const { data: subscription } = useQuery<any>({
    queryKey: ["/api/billing/subscription"],
    queryFn: async () => { const res = await fetch("/api/billing/subscription", { credentials: "include" }); if (!res.ok) return null; return res.json(); },
    staleTime: 60_000, enabled: !!activeBusiness?.id,
  });

  const { data: staffList } = useQuery<any[]>({
    queryKey: ["/api/staff", activeBusiness?.id],
    queryFn: async () => { try { const res = await apiRequest("GET", "/api/staff"); return res.json(); } catch { return []; } },
    staleTime: 60_000,
  });

  useEffect(() => { if (profile) { setForm(seedForm(profile)); setLogoPreview(null); setIsEditing(false); } }, [profile]);

  const update = useMutation({
    mutationFn: async (data: Partial<Form>) => { const res = await apiRequest("PATCH", "/api/business/profile", data); return res.json(); },
    onSuccess: () => {
      toast({ title: "Changes saved" });
      setIsEditing(false);
      qc.invalidateQueries({ queryKey: ["/api/business/profile"] });
      qc.invalidateQueries({ queryKey: ["/api/businesses"] });
    },
    onError: (err: any) => { toast({ title: "Error", description: err?.details?.message || err?.message || "Update failed", variant: "destructive" }); },
  });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => { const fd = new FormData(); fd.append("file", file); const res = await apiRequest("POST", "/api/business/profile/logo", fd); return res.json() as Promise<{ success: boolean; url: string }>; },
    onSuccess: () => { toast({ title: "Logo uploaded" }); qc.invalidateQueries({ queryKey: ["/api/business/profile"] }); qc.invalidateQueries({ queryKey: ["/api/businesses"] }); },
    onError: (err: any) => { setLogoPreview(null); toast({ title: "Upload failed", description: err?.message, variant: "destructive" }); },
  });

  const handleSave = () => { if (!form.name.trim()) { toast({ title: "Business name is required", variant: "destructive" }); return; } update.mutate(form); };
  const handleCancel = () => { if (profile) setForm(seedForm(profile)); setIsEditing(false); };
  const handleLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast({ title: "Max 5MB", variant: "destructive" }); return; }
    const reader = new FileReader(); reader.onload = (ev) => setLogoPreview(ev.target?.result as string); reader.readAsDataURL(file);
    await uploadLogo.mutateAsync(file); if (fileRef.current) fileRef.current.value = "";
  };

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });
  const setSelect = (k: keyof Form) => (v: string) => setForm({ ...form, [k]: v });
  const logoSrc = logoPreview || profile?.logo_url || undefined;
  const d = !isEditing; // disabled flag

  if (!activeBusiness) return <Layout><div className="p-8 text-center text-sm text-gray-400">Select a business first.</div></Layout>;
  if (isLoading) return <Layout><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div></Layout>;

  const planLabel = subscription?.isTrial ? "Free Trial" : (subscription?.planName || subscription?.plan_name || null);
  const planStatus = subscription?.status;
  const isPastDue = planStatus === "past_due";
  const isCanceled = planStatus === "cancelled" || planStatus === "canceled";
  const assignedStaff = (staffList || []).filter((s: any) => s.is_active);
  const industryLabel = INDUSTRIES.find(i => i.value === form.industry)?.label || form.industry;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 py-6 px-4">
        {/* Header */}
        <Card className="rounded-2xl shadow-sm">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploadLogo.isPending} className="relative group focus:outline-none focus:ring-2 focus:ring-primary rounded-2xl">
                <Avatar className="h-20 w-20 rounded-2xl border-2 border-gray-100 shadow-sm">
                  {logoSrc ? <AvatarImage src={logoSrc} alt={form.name} /> : null}
                  <AvatarFallback className="rounded-2xl text-lg font-bold bg-blue-50 text-blue-600">{getInitials(form.name || "B")}</AvatarFallback>
                </Avatar>
                <span className="absolute bottom-0 right-0 bg-primary text-primary-foreground rounded-full p-1.5 shadow border-2 border-white group-hover:scale-110 transition-transform">
                  {uploadLogo.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                </span>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleLogo} className="hidden" />
              </button>
              <div className="flex-1 text-center sm:text-left">
                <h1 className="text-xl font-semibold text-gray-900">{form.name || "Untitled Business"}</h1>
                <p className="text-xs text-gray-400 mt-0.5">ID: {profile?.public_id || profile?.id}</p>
                <div className="flex flex-wrap gap-1.5 mt-2 justify-center sm:justify-start">
                  {form.entityType && <Badge variant="secondary" className="text-[11px]">{form.entityType}</Badge>}
                  {industryLabel && <Badge variant="outline" className="text-[11px]">{industryLabel}</Badge>}
                </div>
              </div>
              <Button variant={isEditing ? "destructive" : "outline"} size="sm" onClick={() => isEditing ? handleCancel() : setIsEditing(true)} className="gap-1.5 shrink-0">
                {isEditing ? <><X className="h-3.5 w-3.5" /> Cancel</> : <><Pencil className="h-3.5 w-3.5" /> Edit</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Business Overview */}
        <Card className="rounded-2xl shadow-sm">
          <CardHeader><CardTitle className="text-sm font-semibold flex items-center gap-2"><Building2 className="h-4 w-4" /> Business Overview</CardTitle></CardHeader>
          <CardContent><div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Business Name <span className="text-red-500">*</span></Label><Input disabled={d} value={form.name} onChange={set("name")} /></div>
            <div><Label>Industry</Label>
              <Select disabled={d} value={form.industry} onValueChange={(v) => {
                const match = INDUSTRIES.find(i => i.value === v);
                setForm({ ...form, industry: v, ...(match ? { naicsCode: match.naics } : {}) });
              }}>
                <SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger>
                <SelectContent>{INDUSTRIES.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Entity Type</Label>
              <Select disabled={d} value={form.entityType} onValueChange={setSelect("entityType")}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{ENTITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
          </div></CardContent>
        </Card>

        {/* Tax */}
        <Card className="rounded-2xl shadow-sm">
          <CardHeader><CardTitle className="text-sm font-semibold flex items-center gap-2"><FileText className="h-4 w-4" /> Tax Information</CardTitle></CardHeader>
          <CardContent><div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>EIN</Label><Input disabled={d} value={form.ein} onChange={set("ein")} placeholder="XX-XXXXXXX" /></div>
            <div><Label>State ID</Label><Input disabled={d} value={form.stateId} onChange={set("stateId")} placeholder="Optional" /></div>
            <div><Label>Formation State</Label>
              <Select disabled={d} value={form.formationState} onValueChange={setSelect("formationState")}>
                <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div></CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="rounded-2xl shadow-sm">
          <CardHeader><CardTitle className="text-sm font-semibold flex items-center gap-2"><Phone className="h-4 w-4" /> Business Contact</CardTitle></CardHeader>
          <CardContent><div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Phone</Label><Input disabled={d} value={form.businessPhone} onChange={set("businessPhone")} placeholder="+1 555 123 4567" /></div>
            <div><Label>Email</Label><Input disabled={d} value={form.businessEmail} onChange={set("businessEmail")} placeholder="info@company.com" /></div>
          </div></CardContent>
        </Card>

        {/* Address */}
        <Card className="rounded-2xl shadow-sm">
          <CardHeader><CardTitle className="text-sm font-semibold flex items-center gap-2"><MapPin className="h-4 w-4" /> Address</CardTitle></CardHeader>
          <CardContent><div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Label>Address Line 1</Label><Input disabled={d} value={form.addressLine1} onChange={set("addressLine1")} /></div>
            <div className="md:col-span-2"><Label>Address Line 2</Label><Input disabled={d} value={form.addressLine2} onChange={set("addressLine2")} /></div>
            <div><Label>City</Label><Input disabled={d} value={form.city} onChange={set("city")} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>State</Label>
                <Select disabled={d} value={form.state} onValueChange={setSelect("state")}>
                  <SelectTrigger><SelectValue placeholder="ST" /></SelectTrigger>
                  <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>ZIP</Label><Input disabled={d} value={form.zip} onChange={set("zip")} /></div>
            </div>
          </div></CardContent>
        </Card>

        {/* Contact Person */}
        <Card className="rounded-2xl shadow-sm">
          <CardHeader><CardTitle className="text-sm font-semibold flex items-center gap-2"><UserCircle className="h-4 w-4" /> Contact Person</CardTitle></CardHeader>
          <CardContent><div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Name</Label><Input disabled={d} value={form.contactName} onChange={set("contactName")} placeholder="John Doe" /></div>
            <div><Label>Role</Label>
              <Select disabled={d} value={form.contactRole} onValueChange={setSelect("contactRole")}>
                <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>{CONTACT_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Email</Label><Input disabled={d} value={form.contactEmail} onChange={set("contactEmail")} placeholder="john@company.com" /></div>
            <div><Label>Phone</Label><Input disabled={d} value={form.contactPhone} onChange={set("contactPhone")} placeholder="+1 555 000 0000" /></div>
          </div></CardContent>
        </Card>

        {/* Assigned Staff */}
        <Card className="rounded-2xl shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Users className="h-4 w-4" /> Assigned Staff</CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigateTo("/staff")} className="text-xs h-7">Manage Staff</Button>
          </CardHeader>
          <CardContent>
            {assignedStaff.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No staff assigned yet. Go to Staff Management to add team members.</p>
            ) : (
              <div className="space-y-2">
                {assignedStaff.slice(0, 5).map((s: any) => (
                  <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-xs font-bold">
                        {(s.first_name?.[0] || s.email[0]).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{s.full_name || s.email}</p>
                        <p className="text-[11px] text-gray-400">{s.email}</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-[10px] capitalize">{s.role}</Badge>
                  </div>
                ))}
                {assignedStaff.length > 5 && <p className="text-[11px] text-gray-400 text-center">+{assignedStaff.length - 5} more</p>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plan & Subscription */}
        <Card className="rounded-2xl shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><CreditCard className="h-4 w-4" /> Plan & Subscription</CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigateTo("/subscription")} className="text-xs h-7">Manage Plan</Button>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{planLabel || "No Plan"}</span>
                  {subscription?.isTrial && <Badge className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0">Trial</Badge>}
                  {isPastDue && <Badge className="text-[10px] bg-red-50 text-red-700 border border-red-200 px-1.5 py-0">Past Due</Badge>}
                  {isCanceled && <Badge className="text-[10px] bg-gray-100 text-gray-600 border border-gray-200 px-1.5 py-0">Expired</Badge>}
                  {subscription && !subscription.isTrial && !isPastDue && !isCanceled && <Badge className="text-[10px] bg-green-50 text-green-700 border border-green-200 px-1.5 py-0">Active</Badge>}
                </div>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {isPastDue ? "Payment overdue — please update billing" : isCanceled ? "Subscription expired" : planStatus === "active" ? "Subscription active" : !planLabel ? "No active subscription" : "Subscription active"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bank Connection */}
        <BankConnectionSection businessId={activeBusiness?.id} />

        {/* Save bar (only in edit mode) */}
        {isEditing && (
          <div className="flex items-center justify-end gap-2 pb-8 sticky bottom-4 z-10">
            <div className="bg-white/90 backdrop-blur-sm border rounded-xl shadow-lg px-4 py-3 flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={update.isPending}><X className="h-4 w-4 mr-1" /> Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={update.isPending || !form.name.trim()}>
                {update.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

// ── Bank Connection Section (inline component) ──────────────────────
function BankConnectionSection({ businessId }: { businessId: number | undefined }) {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const { data: bankStatus, isLoading } = useQuery<any>({
    queryKey: ["bank-status", businessId],
    queryFn: async () => {
      const res = await fetch(`/api/bank-connection/status/${businessId}`, { credentials: "include" });
      if (!res.ok) return { connected: false };
      return res.json();
    },
    enabled: !!businessId,
    staleTime: 30_000,
  });

  const sendRequest = async () => {
    if (!email || !businessId) return;
    setSending(true);
    try {
      const res = await apiRequest("POST", "/api/bank-connection/request", { businessId, recipientEmail: email });
      const data = await res.json();
      if (data.success) {
        setSent(true);
        toast({ title: "Connection request sent", description: `Email sent to ${email}` });
      } else {
        toast({ title: "Failed", description: data.message || "Could not send request", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err?.message || "Request failed", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  if (!businessId) return null;

  return (
    <Card className="rounded-2xl shadow-sm">
      <CardHeader>
        <CardTitle className="text-sm font-semibold flex items-center gap-2"><Landmark className="h-4 w-4" /> Bank Connection</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 py-2">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            <span className="text-xs text-gray-400">Checking bank status...</span>
          </div>
        ) : bankStatus?.connected ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium text-green-700">Connected</span>
            </div>
            {bankStatus.institution && (
              <p className="text-xs text-gray-500">Institution: <span className="font-medium text-gray-700">{bankStatus.institution}</span></p>
            )}
            {bankStatus.lastSyncedAt && (
              <p className="text-xs text-gray-400">Last synced: {new Date(bankStatus.lastSyncedAt).toLocaleDateString()}</p>
            )}
            {bankStatus.connectedAt && !bankStatus.lastSyncedAt && (
              <p className="text-xs text-gray-400">Connected: {new Date(bankStatus.connectedAt).toLocaleDateString()}</p>
            )}
          </div>
        ) : sent ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium text-amber-700">Request Pending</span>
            </div>
            <p className="text-xs text-gray-500">Secure connection link sent to <span className="font-medium">{email}</span></p>
            <p className="text-[11px] text-gray-400">The link expires in 60 minutes. The client will verify their email before connecting.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">No bank connected. Send a secure link to your client to connect their bank via Plaid.</p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Client email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="text-sm h-9"
              />
              <Button size="sm" onClick={sendRequest} disabled={!email || sending} className="gap-1.5 shrink-0 h-9">
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {sending ? "Sending..." : "Send"}
              </Button>
            </div>
            <p className="text-[10px] text-gray-400">Client will receive a secure link with email verification. No bank credentials are shared with you.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
